// client/src/pages/AccessDenied.tsx

import { Card, CardContent, CardHeader, CardTitle, CardDescription, CardFooter } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { ShieldX, ArrowLeft, Home, Mail } from "lucide-react";
import { useLocation } from "wouter";
import { useAuth } from "@/hooks/useAuth";
import { FormattedMessage, useIntl } from "react-intl";

// ============================================================================
// Constants
// ============================================================================

const SUPPORT_EMAIL = "support@alpro.com";
const DASHBOARD_PATH = "/dashboard";

// ============================================================================
// Main Component
// ============================================================================

/**
 * AccessDenied - Página que se muestra cuando el usuario no tiene permisos
 * para acceder a una ruta protegida.
 * 
 * Muestra:
 * - Icono de acceso denegado
 * - Mensaje explicativo
 * - Botón para volver al dashboard
 * - Opción de contactar soporte
 */
export function AccessDenied() {
  const [, setLocation] = useLocation();
  const { user } = useAuth();

  const handleGoBack = () => {
    // Intentar ir atrás en el historial
    if (window.history.length > 1) {
      window.history.back();
    } else {
      setLocation(DASHBOARD_PATH);
    }
  };

  const handleGoToDashboard = () => {
    setLocation(DASHBOARD_PATH);
  };

  const handleContactSupport = () => {
    window.location.href = `mailto:${SUPPORT_EMAIL}?subject=Access Request - ALPRO&body=${encodeURIComponent(`${user?.email ?? 'N/A'}\n${user?.role ?? 'N/A'}`)}`;
  };

  return (
    <div className="min-h-screen w-full flex items-center justify-center bg-background p-4">
      <Card className="w-full max-w-lg">
        <CardHeader className="text-center">
          <div className="mx-auto mb-4 flex h-16 w-16 items-center justify-center rounded-full bg-destructive/10">
            <ShieldX className="h-8 w-8 text-destructive" />
          </div>
          <CardTitle className="text-2xl font-bold text-foreground">
            <FormattedMessage id="accessDenied.title" />
          </CardTitle>
          <CardDescription className="text-muted-foreground">
            <FormattedMessage id="accessDenied.description" />
          </CardDescription>
        </CardHeader>
        
        <CardContent className="space-y-4">
          <div className="rounded-lg bg-muted/50 p-4 text-sm text-muted-foreground">
            <p className="mb-2">
              <strong><FormattedMessage id="accessDenied.whyTitle" /></strong>
            </p>
            <ul className="list-disc list-inside space-y-1">
              <li><FormattedMessage id="accessDenied.reason1" /></li>
              <li><FormattedMessage id="accessDenied.reason2" /></li>
              <li><FormattedMessage id="accessDenied.reason3" /></li>
            </ul>
          </div>

          {user && (
            <div className="rounded-lg border border-border p-4 text-sm">
              <p className="text-muted-foreground">
                <span className="font-medium text-foreground"><FormattedMessage id="accessDenied.userLabel" /></span>{' '}
                {user.email}
              </p>
              <p className="text-muted-foreground mt-1">
                <span className="font-medium text-foreground"><FormattedMessage id="accessDenied.roleLabel" /></span>{' '}
                <span className="capitalize">{user.role}</span>
              </p>
            </div>
          )}
        </CardContent>

        <CardFooter className="flex flex-col gap-3">
          <div className="flex w-full gap-3">
            <Button 
              variant="outline" 
              className="flex-1"
              onClick={handleGoBack}
            >
              <ArrowLeft className="mr-2 h-4 w-4" />
              <FormattedMessage id="accessDenied.goBack" />
            </Button>
            <Button 
              className="flex-1"
              onClick={handleGoToDashboard}
            >
              <Home className="mr-2 h-4 w-4" />
              <FormattedMessage id="accessDenied.goToDashboard" />
            </Button>
          </div>
          
          <Button 
            variant="ghost" 
            className="w-full text-muted-foreground hover:text-foreground"
            onClick={handleContactSupport}
          >
            <Mail className="mr-2 h-4 w-4" />
            <FormattedMessage id="accessDenied.contactAdmin" />
          </Button>
        </CardFooter>
      </Card>
    </div>
  );
}

// ============================================================================
// Compact Version (para usar como fallback en componentes)
// ============================================================================

interface AccessDeniedCompactProps {
  /** Custom message i18n key override */
  message?: string;
  /** Mostrar información del usuario */
  showUserInfo?: boolean;
  /** Callback para acción personalizada */
  onAction?: () => void;
  /** Action button text i18n key override */
  actionText?: string;
}

/**
 * Versión compacta de AccessDenied para usar dentro de layouts
 */
export function AccessDeniedCompact({
  message,
  showUserInfo = false,
  onAction,
  actionText
}: AccessDeniedCompactProps) {
  const { user } = useAuth();
  const intl = useIntl();

  const displayMessage = message ?? intl.formatMessage({ id: "accessDenied.compact.defaultMessage" });
  const displayAction = actionText ?? intl.formatMessage({ id: "accessDenied.compact.requestAccess" });

  return (
    <div className="flex flex-col items-center justify-center p-8 text-center">
      <div className="mb-4 flex h-12 w-12 items-center justify-center rounded-full bg-destructive/10">
        <ShieldX className="h-6 w-6 text-destructive" />
      </div>
      
      <h3 className="text-lg font-semibold text-foreground mb-2">
        <FormattedMessage id="accessDenied.compact.title" />
      </h3>
      
      <p className="text-sm text-muted-foreground mb-4 max-w-sm">
        {displayMessage}
      </p>

      {showUserInfo && user && (
        <p className="text-xs text-muted-foreground mb-4">
          <FormattedMessage id="accessDenied.compact.currentRole" /> <span className="font-medium capitalize">{user.role}</span>
        </p>
      )}

      {onAction && (
        <Button variant="outline" size="sm" onClick={onAction}>
          {displayAction}
        </Button>
      )}
    </div>
  );
}

export default AccessDenied;
